import { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useExperience } from '@/store/experience';

// Gate backdrop: the supplied coin footage loops full-screen until the password is accepted.
// On JUMP the footage rushes toward the viewer and dissolves into the 3D sky.
export function GateScene() {
  const phase = useExperience((s) => s.phase);
  const reduced = useExperience((s) => s.reducedMotion);
  const jumping = phase === 'JUMPING';
  const videoRef = useRef();
  const mx = useMotionValue(0), my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 }), sy = useSpring(my, { stiffness: 40, damping: 20 });
  const x = useTransform(sx, (v) => v * -22), y = useTransform(sy, (v) => v * -14);

  useEffect(() => {
    const onMove = (e) => { mx.set(e.clientX / window.innerWidth - 0.5); my.set(e.clientY / window.innerHeight - 0.5); };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [mx, my]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    window.addEventListener('pointerdown', tryPlay, { once: true });
    return () => window.removeEventListener('pointerdown', tryPlay);
  }, []);

  return (
    <motion.div className="fixed inset-0 z-20 overflow-hidden bg-[#061a3d]" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.9, ease: 'easeIn' } }} transition={{ duration: 1.4 }} data-testid="gate-scene">
      <motion.div className={`absolute inset-[-3%] ${jumping && !reduced ? 'shake' : ''}`} style={{ x, y }}>
        <motion.video ref={videoRef} muted loop playsInline autoPlay preload="auto" poster="/assets/coin_poster.jpg" className="w-full h-full object-cover"
          animate={jumping ? { scale: 1.9, filter: 'blur(8px) brightness(1.5)' } : { scale: [1.02, 1.06, 1.02], filter: 'blur(0px) brightness(1)' }}
          transition={jumping ? { duration: 2.7, ease: [0.55, 0, 0.35, 1] } : { duration: 16, repeat: Infinity, ease: 'easeInOut' }} data-testid="gate-coin-video">
          <source src="/assets/coin.mp4" type="video/mp4" />
          <source src="/assets/coin.webm" type="video/webm" />
        </motion.video>
      </motion.div>
      <div className="absolute inset-0 light-rays pointer-events-none opacity-60" />
      <motion.div className="absolute inset-0 wind-streaks pointer-events-none" animate={{ opacity: jumping ? 0.9 : 0.12 }} transition={{ duration: 1.5 }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(6,26,61,0) 45%, rgba(6,26,61,0.72) 100%)' }} />
      <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(6,26,61,0), rgba(6,26,61,0.55))' }} />
      {jumping && (
        <motion.img src="/assets/skydiver.png" alt="" className="absolute left-1/2 top-[40%] w-[34vmin] -translate-x-1/2 pointer-events-none drop-shadow-2xl"
          initial={{ y: '-30vh', scale: 0.4, opacity: 0, rotate: -6 }}
          animate={{ y: ['-30vh', '0vh', '120vh'], scale: [0.4, 0.7, 0.4], opacity: [0, 1, 1], rotate: [-6, 4, 28] }}
          transition={{ duration: 2.4, delay: 0.3, times: [0, 0.35, 1], ease: ['easeOut', 'easeIn'] }} data-testid="jumper" />
      )}
    </motion.div>
  );
}
